import type {
  CabinConfiguration,
  CabinIssueSeverity,
  ClassMap,
  SeatClass,
} from '../../../domain/models';

/** Display names per seat class. */
export const CLASS_LABELS: ClassMap<string> = {
  ECONOMY: 'Economy',
  PREMIUM_ECONOMY: 'Premium Economy',
  BUSINESS: 'Business',
  FIRST: 'First',
};

export const SEVERITY_LABELS: Readonly<Record<CabinIssueSeverity, string>> = {
  ERROR: 'Error',
  WARNING: 'Warning',
  INFO: 'Info',
};

const INTEGER_FORMAT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const ONE_DECIMAL_FORMAT = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const TWO_DECIMAL_FORMAT = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatInt(value: number): string {
  return Number.isFinite(value) ? INTEGER_FORMAT.format(value) : '–';
}

export function formatOneDecimal(value: number): string {
  return Number.isFinite(value) ? ONE_DECIMAL_FORMAT.format(value) : '–';
}

/** Floor units: whole numbers print bare, fractions print one decimal. */
export function formatUnits(value: number): string {
  if (!Number.isFinite(value)) {
    return '–';
  }
  return Number.isInteger(value) ? INTEGER_FORMAT.format(value) : ONE_DECIMAL_FORMAT.format(value);
}

export function formatKg(value: number): string {
  return Number.isFinite(value) ? `${INTEGER_FORMAT.format(Math.round(value))} kg` : '–';
}

/** Signed value with an explicit plus sign; zero is printed unsigned. */
export function formatSigned(value: number, decimals: 0 | 1 = 0): string {
  if (!Number.isFinite(value)) {
    return '–';
  }
  const formatter = decimals === 0 ? INTEGER_FORMAT : ONE_DECIMAL_FORMAT;
  const rounded = decimals === 0 ? Math.round(value) : Math.round(value * 10) / 10;
  if (rounded === 0) {
    return formatter.format(0);
  }
  return `${rounded > 0 ? '+' : '−'}${formatter.format(Math.abs(rounded))}`;
}

export function formatKgSigned(value: number): string {
  const text = formatSigned(value);
  return text === '–' ? text : `${text} kg`;
}

export function formatKgHr(value: number): string {
  return Number.isFinite(value) ? `${ONE_DECIMAL_FORMAT.format(value)} kg/hr` : '–';
}

export function formatMultiplier(value: number): string {
  return Number.isFinite(value) ? `${TWO_DECIMAL_FORMAT.format(value)}×` : '–';
}

export function formatPitch(inches: number): string {
  return Number.isFinite(inches) ? `${formatUnits(inches)}″` : '–';
}

export function formatPercent(ratio: number): string {
  return Number.isFinite(ratio) ? `${INTEGER_FORMAT.format(Math.round(ratio * 100))}%` : '–';
}

export type ComfortTone = 'poor' | 'fair' | 'good' | 'excellent';

/** Bucket a 0–100 comfort score for colouring. */
export function comfortTone(score: number): ComfortTone {
  if (score >= 80) {
    return 'excellent';
  }
  if (score >= 60) {
    return 'good';
  }
  if (score >= 40) {
    return 'fair';
  }
  return 'poor';
}

export function clampNumber(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Seat count of one class, read straight from the configuration. */
export function seatsOfClass(config: CabinConfiguration, seatClass: SeatClass): number {
  switch (seatClass) {
    case 'ECONOMY':
      return config.economySeats;
    case 'PREMIUM_ECONOMY':
      return config.premiumEconomySeats;
    case 'BUSINESS':
      return config.businessSeats;
    case 'FIRST':
      return config.firstSeats;
  }
}

/** Returns a copy of the configuration with one class's seat count replaced. */
export function withSeatsOfClass(
  config: CabinConfiguration,
  seatClass: SeatClass,
  seats: number
): CabinConfiguration {
  switch (seatClass) {
    case 'ECONOMY':
      return { ...config, economySeats: seats };
    case 'PREMIUM_ECONOMY':
      return { ...config, premiumEconomySeats: seats };
    case 'BUSINESS':
      return { ...config, businessSeats: seats };
    case 'FIRST':
      return { ...config, firstSeats: seats };
  }
}

export function cabinsEqual(a: CabinConfiguration, b: CabinConfiguration): boolean {
  return (
    a.economySeats === b.economySeats &&
    a.economyPitchInches === b.economyPitchInches &&
    a.premiumEconomySeats === b.premiumEconomySeats &&
    a.businessSeats === b.businessSeats &&
    a.businessPitchInches === b.businessPitchInches &&
    a.firstSeats === b.firstSeats &&
    a.galleyCount === b.galleyCount &&
    a.wifiEquipped === b.wifiEquipped &&
    a.inSeatPower === b.inSeatPower
  );
}
